import React, { useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import styled from 'styled-components';
import elearningApi from 'api/elearningApi';
import {
  AuthCard,
  PasswordInput,
  PrimaryButton,
  Alert,
  colors,
} from 'design-system';

const MIN_PASSWORD_LENGTH = 8;

function ForceChangePasswordPage() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // Touched: chỉ hiện inline error sau khi blur, tránh đỏ ngay khi load.
  const [touched, setTouched] = useState({ newPassword: false, confirmPassword: false });

  const validation = useMemo(() => {
    const newTooShort = newPassword.length > 0 && newPassword.length < MIN_PASSWORD_LENGTH;
    const newEmpty = newPassword.length === 0;
    const confirmEmpty = confirmPassword.length === 0;
    const mismatch = confirmPassword.length > 0 && confirmPassword !== newPassword;
    return {
      newTooShort,
      newEmpty,
      confirmEmpty,
      mismatch,
      canSubmit: !newEmpty && !confirmEmpty && !newTooShort && !mismatch,
    };
  }, [newPassword, confirmPassword]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setTouched({ newPassword: true, confirmPassword: true });

    if (validation.newEmpty || validation.confirmEmpty) {
      setError('Vui lòng nhập đầy đủ các trường.');
      return;
    }
    if (validation.newTooShort) {
      setError(`Mật khẩu phải có ít nhất ${MIN_PASSWORD_LENGTH} ký tự.`);
      return;
    }
    if (validation.mismatch) {
      setError('Mật khẩu mới và xác nhận mật khẩu không khớp.');
      return;
    }

    const username =
      JSON.parse(localStorage.getItem('moodleSiteInfo') || '{}')?.username || null;
    if (!username) {
      setError('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.');
      return;
    }

    try {
      setLoading(true);
      // Phase 2D step 2: backend uses sessionToken to authenticate the
      // rotation, then re-fetches a fresh wstoken under the new password.
      // Browser never sees a Moodle credential during this flow.
      await elearningApi.forceChangeMyPassword({ username, newPassword });
      localStorage.setItem('forcePasswordChange', '0');
      setSuccess('Đổi mật khẩu thành công! Bạn sẽ được chuyển hướng...');
      setTimeout(() => {
        window.location.href = '/';
      }, 1500);
    } catch (err) {
      console.error('Lỗi đổi mật khẩu:', err);
      const msg = err?.response?.data?.message || err?.message || 'Đã có lỗi xảy ra khi đổi mật khẩu.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageWrap>
      <Container className='py-4 py-md-5'>
        <AuthCard
          title='Đổi mật khẩu lần đầu'
          subtitle={`Bạn cần đổi mật khẩu mặc định trước khi tiếp tục.`}
        >
          {error && <Alert variant='danger'>{error}</Alert>}
          {success && <Alert variant='success'>{success}</Alert>}

          <form onSubmit={handleSubmit} noValidate>
            <PasswordInput
              label='Mật khẩu mới'
              required
              placeholder={`Tối thiểu ${MIN_PASSWORD_LENGTH} ký tự`}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
              error={
                touched.newPassword && validation.newTooShort
                  ? `Mật khẩu phải có ít nhất ${MIN_PASSWORD_LENGTH} ký tự.`
                  : ''
              }
              disabled={loading}
            />

            <PasswordInput
              label='Nhập lại mật khẩu mới'
              required
              placeholder='Nhập lại mật khẩu vừa tạo'
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
              error={
                touched.confirmPassword && validation.mismatch
                  ? 'Mật khẩu không khớp.'
                  : ''
              }
              disabled={loading}
            />

            <PrimaryButton
              type='submit'
              block
              size='lg'
              loading={loading}
              loadingText='Đang cập nhật...'
              disabled={!validation.canSubmit}
            >
              Cập nhật mật khẩu
            </PrimaryButton>
          </form>
        </AuthCard>
      </Container>
    </PageWrap>
  );
}

const PageWrap = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  /* Nhường padding đầu/cuối để khi nội dung cao hơn viewport (mobile
     bàn phím bật) vẫn cuộn được, không bị kẹt giữa. */
  padding: 24px 0;
`;

export default ForceChangePasswordPage;
